import React, { useEffect, useRef, useState } from 'react';
import { Flex, InputRef } from 'antd';
import { useQueryClient } from 'react-query';

import { trackEvent } from 'jf/analytics/Analytics';
import { StudyClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { useJFOverlay } from 'jf/common/useJFOverlay';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExInput } from 'jf/components/DevExInput';
import { DevExModal } from 'jf/components/DevExModal';
import { useClientMutation } from 'jf/utils/useClientQuery';

type DeleteStudyModalProps = {
  studyRef: string;
  studyName: string;
};

export const DeleteStudyModal: React.FC<DeleteStudyModalProps> = (props) => {
  const modal = useJFOverlay(DeleteStudyModal);
  const theme = useDevExTheme();
  const queryClient = useQueryClient();

  const inputRef = useRef<InputRef>(null);
  const [input, setInput] = useState('');

  const { mutateAsync: deleteStudy, isLoading: isDeleting } = useClientMutation(
    StudyClient.deleteStudy
  );

  const onCancel = () => {
    if (!isDeleting) {
      modal.close();
    }
  };

  const onOk = () => {
    // Prevent duplicate clicks while already deleting
    if (isDeleting) {
      return;
    }

    trackEvent('survey-list:survey-actions:delete', {
      surveyRef: props.studyRef,
    });
    if (props.studyRef) {
      deleteStudy({ studyRef: props.studyRef })
        .then(() => {
          queryClient.invalidateQueries('GET_STUDIES');
          queryClient.invalidateQueries('GET_STUDY_SEQUENCES');
        })
        .finally(() => {
          modal.close(true);
        });
    }
  };

  // reset input after reopening modal
  useEffect(() => {
    if (modal.isOpened) {
      setInput('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [modal.isOpened]);

  // ensure repeating spaces are displayed
  const safeStudyName = props.studyName.replace(/ /g, '\u00A0');

  return (
    <DevExModal
      open={modal.isOpened}
      onCancel={onCancel}
      onOk={onOk}
      title="Delete Survey"
      footer={
        <>
          <DevExButton onClick={onCancel} disabled={isDeleting}>
            Cancel
          </DevExButton>
          <DevExButton
            type="primary"
            onClick={onOk}
            loading={isDeleting}
            disabled={input !== props.studyName}
          >
            Delete
          </DevExButton>
        </>
      }
      width={500}
    >
      <Flex vertical gap={theme.variable.spacing.sm}>
        <div>
          Once you delete this survey, you will not be able to recover it. It will be deleted
          forever.
        </div>
        <div>Please type "{safeStudyName}" to confirm.</div>
        <DevExInput ref={inputRef} value={input} onChange={setInput} />
      </Flex>
    </DevExModal>
  );
};
